import { useState } from "react";
import { useModal } from "@ebay/nice-modal-react";
import { t } from "@/i18n-js/instance";
import { FiltersBar } from "@/react/components/SettingsApp/CommunityBot/FiltersBar";
import {
  SpaceGroupsContextProvider,
  SpacesContextProvider,
} from "@circle-react/contexts";
import {
  USAGE_SECTIONS,
  UsageContextProvider,
} from "@circle-react/providers/UsageContext";
import { Modal } from "@circle-react-uikit/ModalV2";
import { ImportModalFooter } from "../ImportModalFooter";
import { ImportModalHeader } from "../ImportModalHeader";
import { useSpacesForImport } from "../queries";
import { useMultiSelect } from "../useMultiSelect";
import { Spaces } from "./Spaces";
import { COMMUNITY_TYPE_OPTIONS } from "./constants";

export const ImportSpacesModalComponent = ({
  spaceIds,
  onImport,
  page,
}: {
  spaceIds: number[];
  onImport: (ids: number[]) => void;
  page: number;
}) => {
  const modal = useModal();
  const [currentPage, setPage] = useState(page);
  const [filters, setFilters] = useState({
    name: "",
    type: "",
    spaceGroup: "",
  });

  const spacesQuery = useSpacesForImport({
    currentPage,
    spaceIds,
    name: filters.name,
    type: filters.type,
    spaceGroup: filters.spaceGroup,
  });
  const {
    selectedIds,
    allIdsOnPage,
    handleCheckboxChange,
    handleSelectAllOnPage,
    reset,
  } = useMultiSelect({ query: spacesQuery });

  const onChangePage = (page: number) => {
    // Forcing useQuery to only cache one page
    setPage(page);
    spacesQuery.remove();
  };

  const handleOnImport = () => {
    onImport(selectedIds);
    void modal.hide();
    reset();
  };

  return (
    <SpaceGroupsContextProvider>
      <SpacesContextProvider>
        <UsageContextProvider section={USAGE_SECTIONS.ADMIN}>
          <Modal isOpen={modal.visible} onClose={modal.hide}>
            <Modal.Overlay />
            <Modal.Content
              size="auto"
              className="flex h-full max-h-full max-w-[768px] flex-col overflow-visible !rounded-t-none sm:h-full sm:!rounded-t-lg md:max-h-[80vh] lg:max-w-[992px]"
            >
              <ImportModalHeader
                title={t(
                  "community_bot.bot_builder.knowledge_page.community_resources_table.title",
                )}
              />
              <FiltersBar
                name={filters.name}
                onNameChange={name => setFilters({ ...filters, name })}
                type={filters.type}
                onTypeChange={type => setFilters({ ...filters, type })}
                typeOptions={COMMUNITY_TYPE_OPTIONS}
                spaceGroup={filters.spaceGroup}
                onSpaceGroupChange={spaceGroup =>
                  setFilters({ ...filters, spaceGroup })
                }
                showSpaceGroup
                isModal
              />
              <div className="flex-1 overflow-auto">
                <Spaces
                  spacesQuery={spacesQuery}
                  selectedIds={selectedIds}
                  handleCheckboxChange={handleCheckboxChange}
                  handleSelectAllOnPage={handleSelectAllOnPage}
                  allIdsOnPage={allIdsOnPage}
                  onChangePage={onChangePage}
                  hasFilters={Boolean(
                    filters.name || filters.type || filters.spaceGroup,
                  )}
                />
              </div>
              <ImportModalFooter
                onImport={handleOnImport}
                selectedCount={selectedIds.length}
                count={spacesQuery.data?.count || 0}
              />
            </Modal.Content>
          </Modal>
        </UsageContextProvider>
      </SpacesContextProvider>
    </SpaceGroupsContextProvider>
  );
};
